import {
  CapsuleGeometry,
  ConeGeometry,
  CylinderGeometry,
  DoubleSide,
  Group,
  Mesh,
  MeshStandardMaterial,
  Object3D,
  SphereGeometry,
  TorusGeometry,
} from "three";

import type {
  MannequinMotionConfig,
  RenderVector3,
} from "../types.ts";
import { CHARACTER_PRESENTATION_CONFIG } from "../../config/character.ts";
import { APPEARANCE_PRESENTATION_CONFIG } from "../../config/appearance.ts";
import {
  DEFAULT_PLAYER_APPEARANCE,
  resolvePresentedHairStyle,
  type CharacterAppearance,
} from "../../sim/appearance.ts";
import {
  applyGarmentLayerSet,
  createGarmentGeometrySet,
  createGarmentLayerSet,
  setGarmentTrimColour,
  type GarmentGeometrySet,
  type GarmentLayerSet,
} from "./garments.ts";
import {
  createCharacterFeatureGeometrySet,
  type CharacterFeatureGeometrySet,
} from "./features.ts";
import {
  createLowerBodyGeometrySet,
  footwearBottomHeight,
  type LowerBodyGeometrySet,
} from "./lowerBody.ts";

interface JointSet {
  readonly hips: Mesh;
  readonly torso: Group;
  readonly leftArm: Group;
  readonly rightArm: Group;
  readonly leftLeg: Group;
  readonly rightLeg: Group;
}

interface MannequinGeometry {
  readonly root: Group;
  readonly presentationRoot: Group;
  readonly joints: JointSet;
  readonly appearanceParts: AppearanceParts;
  readonly materialSet: MannequinMaterials;
  readonly materials: readonly MeshStandardMaterial[];
  readonly garmentGeometries: GarmentGeometrySet;
  readonly featureGeometries: CharacterFeatureGeometrySet;
  readonly lowerBodyGeometries: LowerBodyGeometrySet;
}

interface MannequinMaterials {
  readonly outerwear: MeshStandardMaterial;
  readonly lower: MeshStandardMaterial;
  readonly skin: MeshStandardMaterial;
  readonly boot: MeshStandardMaterial;
  readonly hair: MeshStandardMaterial;
  readonly eye: MeshStandardMaterial;
  readonly accent: MeshStandardMaterial;
  readonly accessory: MeshStandardMaterial;
  readonly trim: MeshStandardMaterial;
}

interface AppearanceParts {
  readonly hips: Mesh;
  readonly collar: Mesh;
  readonly head: Mesh;
  readonly nose: Mesh;
  readonly mouth: Mesh;
  readonly leftBrow: Mesh;
  readonly rightBrow: Mesh;
  readonly leftEye: Mesh;
  readonly rightEye: Mesh;
  readonly hair: Mesh;
  readonly leftLens: Mesh;
  readonly rightLens: Mesh;
  readonly headwear: Mesh;
  readonly neckwear: Mesh;
  readonly skirt: Mesh;
  readonly garments: GarmentLayerSet;
  readonly belly: Mesh;
  readonly carried: Mesh;
  readonly leftArm: Group;
  readonly rightArm: Group;
  readonly leftSleeve: Mesh;
  readonly rightSleeve: Mesh;
  readonly leftHand: Mesh;
  readonly rightHand: Mesh;
  readonly leftLeg: Group;
  readonly rightLeg: Group;
  readonly leftTrouser: Mesh;
  readonly rightTrouser: Mesh;
  readonly leftFootwear: Mesh;
  readonly rightFootwear: Mesh;
}

export type MannequinMotionState =
  | "idle"
  | "walk"
  | "run"
  | "jump"
  | "fall"
  | "seated"
  | "driving";

export interface MannequinMotionSample {
  readonly planarSpeedMetersPerSecond: number;
  readonly verticalSpeedMetersPerSecond: number;
  readonly grounded: boolean;
  readonly isSprinting: boolean;
  readonly animationTimeSeconds: number;
  readonly distanceTravelledMeters: number;
}

export interface MannequinPose {
  readonly state: MannequinMotionState;
  readonly leftArmX: number;
  readonly rightArmX: number;
  readonly leftArmZ: number;
  readonly rightArmZ: number;
  readonly leftLegX: number;
  readonly rightLegX: number;
  readonly torsoRoll: number;
  readonly torsoPitch: number;
  readonly weightShiftMeters: number;
  readonly rootBobMeters: number;
  readonly hipsYaw: number;
}

const LEG_PIVOT_HEIGHT_M = 0.78;
const FOOTWEAR_LOCAL_Y_M = -0.72;
const FOOTWEAR_LOCAL_Z_M = 0.065;
const HEAD_RADIUS_M =
  0.18 * CHARACTER_PRESENTATION_CONFIG.proportions.headScale;

function createMaterials(): MannequinMaterials {
  return {
    outerwear: new MeshStandardMaterial({
      name: "mannequin-coat",
      color: 0x7f553d,
      roughness: 0.94,
      metalness: 0,
    }),
    lower: new MeshStandardMaterial({
      name: "mannequin-cloth",
      color: 0x343b3b,
      roughness: 0.96,
      metalness: 0,
    }),
    skin: new MeshStandardMaterial({
      name: "mannequin-skin",
      color: 0xb69a7d,
      roughness: 0.9,
      metalness: 0,
    }),
    boot: new MeshStandardMaterial({
      name: "mannequin-boot",
      color: 0x232725,
      roughness: 0.88,
      metalness: 0,
    }),
    hair: new MeshStandardMaterial({
      name: "mannequin-hair",
      color: 0x5d3d32,
      roughness: 0.96,
      metalness: 0,
    }),
    eye: new MeshStandardMaterial({
      name: "mannequin-eyes",
      color: 0x6f7b7c,
      roughness: 0.78,
      metalness: 0,
    }),
    accent: new MeshStandardMaterial({
      name: "mannequin-accent",
      color: 0xb88959,
      roughness: 0.9,
      metalness: 0,
    }),
    accessory: new MeshStandardMaterial({
      name: "mannequin-accessory",
      color: 0x343735,
      roughness: 0.82,
      metalness: 0,
    }),
    trim: new MeshStandardMaterial({
      name: "mannequin-garment-trim",
      color: 0x51382f,
      roughness: 0.97,
      metalness: 0,
      side: DoubleSide,
    }),
  };
}

function enableShadows(object: Object3D): void {
  object.traverse((child) => {
    if (child instanceof Mesh) {
      child.castShadow = true;
      child.receiveShadow = true;
    }
  });
}

function createHangingSegment(
  geometry: import("three").BufferGeometry,
  material: MeshStandardMaterial,
): { readonly joint: Group; readonly trouser: Mesh } {
  const pivot = new Group();
  const trouser = new Mesh(geometry, material);
  pivot.add(trouser);
  return { joint: pivot, trouser };
}

function createSleeveJoint(
  geometry: import("three").BufferGeometry,
  material: MeshStandardMaterial,
): { readonly joint: Group; readonly sleeve: Mesh } {
  const joint = new Group();
  const sleeveMesh = new Mesh(geometry, material);
  sleeveMesh.name = "sleeve";
  joint.add(sleeveMesh);
  return { joint, sleeve: sleeveMesh };
}

function createMannequinGeometry(): MannequinGeometry {
  const materialSet = createMaterials();
  const {
    outerwear: coatMaterial,
    lower: clothMaterial,
    skin: skinMaterial,
    boot: bootMaterial,
    hair: hairMaterial,
    eye: eyeMaterial,
    accent: accentMaterial,
    accessory: accessoryMaterial,
    trim: trimMaterial,
  } = materialSet;
  const root = new Group();
  root.name = "player-mannequin";
  const presentationRoot = new Group();
  presentationRoot.name = "player-mannequin-presentation";
  root.add(presentationRoot);

  const hips = new Mesh(
    new CylinderGeometry(0.18, 0.21, 0.24, 6),
    clothMaterial,
  );
  hips.name = "hips";
  hips.position.y = 0.86;
  presentationRoot.add(hips);

  const upperBody = new Group();
  upperBody.name = "upper-body";
  upperBody.position.y = 1.28;
  presentationRoot.add(upperBody);

  const collar = new Mesh(
    new CylinderGeometry(0.17, 0.2, 0.12, 6),
    clothMaterial,
  );
  collar.name = "collar";
  collar.position.y = 0.31;
  upperBody.add(collar);

  const garmentGeometries = createGarmentGeometrySet();
  const featureGeometries = createCharacterFeatureGeometrySet(HEAD_RADIUS_M);
  const lowerBodyGeometries = createLowerBodyGeometrySet(0.74);
  const garments = createGarmentLayerSet(
    garmentGeometries,
    coatMaterial,
    trimMaterial,
    accentMaterial,
    "player-garments",
  );
  upperBody.add(garments.object);

  const belly = new Mesh(
    new SphereGeometry(0.22, 8, 5),
    coatMaterial,
  );
  belly.name = "belly-shape";
  belly.position.set(0, -0.065, 0.205);
  belly.scale.set(0.76, 0.86, 0.58);
  belly.visible = false;
  upperBody.add(belly);

  const head = new Mesh(
    new SphereGeometry(HEAD_RADIUS_M, 12, 9),
    skinMaterial,
  );
  head.name = "head";
  head.position.y = 1.79;
  presentationRoot.add(head);

  const nose = new Mesh(
    new ConeGeometry(0.052, 0.14, 6),
    skinMaterial,
  );
  nose.name = "nose";
  nose.position.set(0, 1.785, HEAD_RADIUS_M * 0.98);
  nose.rotation.x = Math.PI * 0.5;
  presentationRoot.add(nose);

  const leftEye = new Mesh(
    new SphereGeometry(0.029, 7, 5),
    eyeMaterial,
  );
  leftEye.name = "left-eye";
  leftEye.position.set(0.068, 1.825, HEAD_RADIUS_M * 0.91);
  presentationRoot.add(leftEye);

  const rightEye = new Mesh(
    new SphereGeometry(0.029, 7, 5),
    eyeMaterial,
  );
  rightEye.name = "right-eye";
  rightEye.position.set(-0.068, 1.825, HEAD_RADIUS_M * 0.91);
  presentationRoot.add(rightEye);

  const mouth = new Mesh(
    new TorusGeometry(0.04, 0.006, 4, 9, Math.PI),
    accessoryMaterial,
  );
  mouth.name = "mouth";
  mouth.position.set(0, 1.73, HEAD_RADIUS_M * 0.96);
  mouth.rotation.z = Math.PI;
  presentationRoot.add(mouth);

  const browGeometry = new CapsuleGeometry(0.007, 0.052, 1, 4);
  browGeometry.rotateZ(Math.PI * 0.5);
  const leftBrow = new Mesh(browGeometry, hairMaterial);
  leftBrow.name = "left-brow";
  leftBrow.position.set(0.068, 1.872, HEAD_RADIUS_M * 0.94);
  leftBrow.rotation.z = -0.08;
  presentationRoot.add(leftBrow);
  const rightBrow = new Mesh(browGeometry, hairMaterial);
  rightBrow.name = "right-brow";
  rightBrow.position.set(-0.068, 1.872, HEAD_RADIUS_M * 0.94);
  rightBrow.rotation.z = 0.08;
  presentationRoot.add(rightBrow);

  const hair = new Mesh(
    featureGeometries.hair.parted.none,
    hairMaterial,
  );
  hair.name = "hair";
  hair.position.y = 1.79;
  presentationRoot.add(hair);

  const lensGeometry = featureGeometries.eyewear.round;
  const leftLens = new Mesh(lensGeometry, accessoryMaterial);
  leftLens.name = "left-lens";
  leftLens.position.set(0.068, 1.825, HEAD_RADIUS_M * 1.04);
  presentationRoot.add(leftLens);
  const rightLens = new Mesh(lensGeometry, accessoryMaterial);
  rightLens.name = "right-lens";
  rightLens.position.set(-0.068, 1.825, HEAD_RADIUS_M * 1.04);
  presentationRoot.add(rightLens);

  const headwear = new Mesh(
    featureGeometries.headwear.knit_cap,
    accentMaterial,
  );
  headwear.name = "headwear";
  headwear.position.y = 1.79;
  presentationRoot.add(headwear);

  const neckwear = new Mesh(
    featureGeometries.neckwear.scarf,
    accentMaterial,
  );
  neckwear.name = "neckwear";
  neckwear.position.y = 1.59;
  presentationRoot.add(neckwear);

  const skirt = new Mesh(
    new CylinderGeometry(0.19, 0.29, 0.56, 7),
    clothMaterial,
  );
  skirt.name = "skirt";
  skirt.position.y = 0.62;
  presentationRoot.add(skirt);

  const carried = new Mesh(
    featureGeometries.carried.satchel,
    accentMaterial,
  );
  carried.name = "carried-accessory";
  carried.position.set(0.34, 0.94, -0.03);
  presentationRoot.add(carried);

  const leftSleeveJoint = createSleeveJoint(
    garmentGeometries.wool_coat.sleeve,
    coatMaterial,
  );
  const leftArm = leftSleeveJoint.joint;
  leftArm.name = "left-arm";
  leftArm.position.set(
    CHARACTER_PRESENTATION_CONFIG.proportions.shoulderJointOffsetMeters,
    1.48,
    0,
  );
  const handGeometry = new SphereGeometry(0.062, 7, 5);
  const leftHand = new Mesh(handGeometry, skinMaterial);
  leftHand.name = "left-hand";
  leftHand.scale.set(0.86, 1, 0.72);
  leftArm.add(leftHand);
  presentationRoot.add(leftArm);

  const rightSleeveJoint = createSleeveJoint(
    garmentGeometries.wool_coat.sleeve,
    coatMaterial,
  );
  const rightArm = rightSleeveJoint.joint;
  rightArm.name = "right-arm";
  rightArm.position.set(
    -CHARACTER_PRESENTATION_CONFIG.proportions.shoulderJointOffsetMeters,
    1.48,
    0,
  );
  const rightHand = new Mesh(handGeometry, skinMaterial);
  rightHand.name = "right-hand";
  rightHand.scale.set(0.86, 1, 0.72);
  rightArm.add(rightHand);
  presentationRoot.add(rightArm);

  const leftLegParts = createHangingSegment(
    lowerBodyGeometries.legs.trousers,
    clothMaterial,
  );
  const leftLeg = leftLegParts.joint;
  leftLeg.name = "left-leg";
  leftLegParts.trouser.name = "left-trouser";
  leftLeg.position.set(0.12, LEG_PIVOT_HEIGHT_M, 0);
  presentationRoot.add(leftLeg);

  const rightLegParts = createHangingSegment(
    lowerBodyGeometries.legs.trousers,
    clothMaterial,
  );
  const rightLeg = rightLegParts.joint;
  rightLeg.name = "right-leg";
  rightLegParts.trouser.name = "right-trouser";
  rightLeg.position.set(-0.12, LEG_PIVOT_HEIGHT_M, 0);
  presentationRoot.add(rightLeg);

  const leftBoot = new Mesh(
    lowerBodyGeometries.footwear.walking_boots,
    bootMaterial,
  );
  leftBoot.name = "left-boot";
  leftBoot.position.set(0, FOOTWEAR_LOCAL_Y_M, FOOTWEAR_LOCAL_Z_M);
  leftLeg.add(leftBoot);

  const rightBoot = new Mesh(
    lowerBodyGeometries.footwear.walking_boots,
    bootMaterial,
  );
  rightBoot.name = "right-boot";
  rightBoot.position.set(0, FOOTWEAR_LOCAL_Y_M, FOOTWEAR_LOCAL_Z_M);
  rightLeg.add(rightBoot);

  enableShadows(root);
  return {
    root,
    presentationRoot,
    joints: {
      hips,
      torso: upperBody,
      leftArm,
      rightArm,
      leftLeg,
      rightLeg,
    },
    appearanceParts: {
      hips,
      collar,
      head,
      nose,
      mouth,
      leftBrow,
      rightBrow,
      leftEye,
      rightEye,
      hair,
      leftLens,
      rightLens,
      headwear,
      neckwear,
      skirt,
      garments,
      belly,
      carried,
      leftArm,
      rightArm,
      leftSleeve: leftSleeveJoint.sleeve,
      rightSleeve: rightSleeveJoint.sleeve,
      leftHand,
      rightHand,
      leftLeg,
      rightLeg,
      leftTrouser: leftLegParts.trouser,
      rightTrouser: rightLegParts.trouser,
      leftFootwear: leftBoot,
      rightFootwear: rightBoot,
    },
    materialSet,
    materials: Object.values(materialSet),
    garmentGeometries,
    featureGeometries,
    lowerBodyGeometries,
  };
}

function assertPositive(value: number, name: string): void {
  if (!Number.isFinite(value) || value <= 0) {
    throw new Error(`${name} must be finite and positive`);
  }
}

function presentationGroundOffsetM(
  pose: Readonly<MannequinPose>,
  appearance: Readonly<CharacterAppearance>,
  footwearGeometry: import("three").BufferGeometry,
): number {
  const limbScale = APPEARANCE_PRESENTATION_CONFIG
    .bodyScaleByShape[appearance.bodyShape].limb;
  const stature = APPEARANCE_PRESENTATION_CONFIG
    .statureScaleById[appearance.stature];
  const lowestBootM = Math.min(
    footwearBottomHeight(
      footwearGeometry,
      LEG_PIVOT_HEIGHT_M,
      FOOTWEAR_LOCAL_Y_M,
      FOOTWEAR_LOCAL_Z_M,
      pose.leftLegX,
      stature,
      limbScale,
    ),
    footwearBottomHeight(
      footwearGeometry,
      LEG_PIVOT_HEIGHT_M,
      FOOTWEAR_LOCAL_Y_M,
      FOOTWEAR_LOCAL_Z_M,
      pose.rightLegX,
      stature,
      limbScale,
    ),
  );
  return CHARACTER_PRESENTATION_CONFIG.soleClearanceMeters +
    Math.max(0, -lowestBootM);
}

export function validateMannequinMotionConfig(
  config: Readonly<MannequinMotionConfig>,
): void {
  assertPositive(config.idleCycleHz, "idleCycleHz");
  assertPositive(config.walkStrideMeters, "walkStrideMeters");
  assertPositive(config.runStrideMeters, "runStrideMeters");
  assertPositive(
    config.locomotionThresholdMetersPerSecond,
    "locomotionThresholdMetersPerSecond",
  );
  assertPositive(
    config.verticalStateThresholdMetersPerSecond,
    "verticalStateThresholdMetersPerSecond",
  );
  for (const [name, value] of Object.entries(config)) {
    if (!Number.isFinite(value) || value < 0) {
      throw new Error(`${name} must be finite and non-negative`);
    }
  }
}

export function classifyMannequinMotion(
  sample: Readonly<MannequinMotionSample>,
  verticalThresholdMetersPerSecond: number,
  locomotionThresholdMetersPerSecond = Number.EPSILON,
): MannequinMotionState {
  if (!sample.grounded) {
    return sample.verticalSpeedMetersPerSecond >
      verticalThresholdMetersPerSecond
      ? "jump"
      : "fall";
  }
  if (
    sample.planarSpeedMetersPerSecond <= locomotionThresholdMetersPerSecond
  ) {
    return "idle";
  }
  return sample.isSprinting ? "run" : "walk";
}

export function sampleMannequinPose(
  sample: Readonly<MannequinMotionSample>,
  config: Readonly<MannequinMotionConfig>,
): MannequinPose {
  const state = classifyMannequinMotion(
    sample,
    config.verticalStateThresholdMetersPerSecond,
    config.locomotionThresholdMetersPerSecond,
  );
  if (state === "idle") {
    const phase =
      sample.animationTimeSeconds * config.idleCycleHz * Math.PI * 2;
    const sway = Math.sin(phase) * config.idleSwayRadians;
    return {
      state,
      leftArmX: sway * 0.25,
      rightArmX: -sway * 0.25,
      leftArmZ: 0,
      rightArmZ: 0,
      leftLegX: 0,
      rightLegX: 0,
      torsoRoll: sway,
      torsoPitch: Math.abs(Math.sin(phase * 2)) * 0.008,
      weightShiftMeters:
        Math.sin(phase) * config.idleWeightShiftMeters,
      rootBobMeters:
        (Math.sin(phase * 2 - Math.PI * 0.5) + 1) *
        0.5 * config.idleBreathMeters,
      hipsYaw: -sway * 0.5,
    };
  }
  if (state === "walk" || state === "run") {
    const strideMeters = state === "run"
      ? config.runStrideMeters
      : config.walkStrideMeters;
    const amplitude = state === "run"
      ? config.runSwingRadians
      : config.walkSwingRadians;
    const phase =
      (sample.distanceTravelledMeters / strideMeters) * Math.PI * 2;
    const swing = Math.sin(phase) * amplitude;
    const bobMeters = state === "run"
      ? config.runBobMeters
      : config.walkBobMeters;
    return {
      state,
      leftArmX: -swing,
      rightArmX: swing,
      leftArmZ: 0,
      rightArmZ: 0,
      leftLegX: swing,
      rightLegX: -swing,
      torsoRoll: -swing * 0.035,
      torsoPitch: state === "run"
        ? config.runLeanRadians
        : config.walkLeanRadians,
      weightShiftMeters: Math.sin(phase) * 0.012,
      rootBobMeters: Math.abs(Math.sin(phase)) * bobMeters,
      hipsYaw: swing * 0.12,
    };
  }
  if (state === "jump") {
    return {
      state,
      leftArmX: -config.airborneArmRadians,
      rightArmX: -config.airborneArmRadians,
      leftArmZ: 0,
      rightArmZ: 0,
      leftLegX: config.jumpLegRadians,
      rightLegX: config.jumpLegRadians,
      torsoRoll: 0,
      torsoPitch: 0.045,
      weightShiftMeters: 0,
      rootBobMeters: 0,
      hipsYaw: 0,
    };
  }
  return {
    state,
    leftArmX: 0,
    rightArmX: 0,
    leftArmZ: config.airborneArmRadians,
    rightArmZ: -config.airborneArmRadians,
    leftLegX: config.fallLegRadians,
    rightLegX: -config.fallLegRadians,
    torsoRoll: 0,
    torsoPitch: -0.025,
    weightShiftMeters: 0,
    rootBobMeters: 0,
    hipsYaw: 0,
  };
}

const NEUTRAL_POSE: MannequinPose = {
  state: "idle",
  leftArmX: 0,
  rightArmX: 0,
  leftArmZ: 0,
  rightArmZ: 0,
  leftLegX: 0,
  rightLegX: 0,
  torsoRoll: 0,
  torsoPitch: 0,
  weightShiftMeters: 0,
  rootBobMeters: 0,
  hipsYaw: 0,
};

export class MannequinView {
  readonly object: Group;
  private readonly presentationRoot: Group;
  private readonly joints: JointSet;
  private readonly appearanceParts: AppearanceParts;
  private readonly materialSet: MannequinMaterials;
  private readonly materials: readonly MeshStandardMaterial[];
  private readonly garmentGeometries: GarmentGeometrySet;
  private readonly featureGeometries: CharacterFeatureGeometrySet;
  private readonly lowerBodyGeometries: LowerBodyGeometrySet;
  private readonly motionConfig: Readonly<MannequinMotionConfig> | undefined;
  private currentMotionState: MannequinMotionState = "idle";
  private statureScale = 1;
  private appearance: Readonly<CharacterAppearance> = DEFAULT_PLAYER_APPEARANCE;

  constructor(
    motionConfig?: Readonly<MannequinMotionConfig>,
    appearance: Readonly<CharacterAppearance> = DEFAULT_PLAYER_APPEARANCE,
  ) {
    if (motionConfig !== undefined) {
      validateMannequinMotionConfig(motionConfig);
    }
    const mannequin = createMannequinGeometry();
    this.object = mannequin.root;
    this.presentationRoot = mannequin.presentationRoot;
    this.joints = mannequin.joints;
    this.appearanceParts = mannequin.appearanceParts;
    this.materialSet = mannequin.materialSet;
    this.materials = mannequin.materials;
    this.garmentGeometries = mannequin.garmentGeometries;
    this.featureGeometries = mannequin.featureGeometries;
    this.lowerBodyGeometries = mannequin.lowerBodyGeometries;
    this.motionConfig = motionConfig;
    this.setAppearance(appearance);
  }

  setAppearance(appearance: Readonly<CharacterAppearance>): void {
    this.appearance = appearance;
    const body =
      APPEARANCE_PRESENTATION_CONFIG.bodyScaleByShape[appearance.bodyShape];
    const face =
      APPEARANCE_PRESENTATION_CONFIG.faceScaleByShape[appearance.faceShape];
    const outerwear = APPEARANCE_PRESENTATION_CONFIG
      .outerwearSilhouetteById[appearance.outerwear];
    const age =
      APPEARANCE_PRESENTATION_CONFIG.agePresentationById[appearance.ageGroup];
    const parts = this.appearanceParts;

    this.statureScale =
      APPEARANCE_PRESENTATION_CONFIG.statureScaleById[appearance.stature];
    parts.hips.scale.set(body.hip, 1, body.hip);
    parts.belly.visible = body.belly > 0.25;
    parts.belly.position.z = 0.18 + body.belly * 0.025;
    parts.belly.scale.set(
      body.torso * (0.58 + body.belly * 0.08),
      0.7 + body.belly * 0.1,
      body.depth * (0.26 + body.belly * 0.15),
    );
    parts.collar.scale.set(body.shoulder, 1, body.shoulder);
    const shoulderJointOffset = CHARACTER_PRESENTATION_CONFIG.proportions
      .shoulderJointOffsetMeters * body.shoulder;
    parts.leftArm.position.x = shoulderJointOffset;
    parts.rightArm.position.x = -shoulderJointOffset;
    const sleeveWidth = body.limb * outerwear.sleeveWidth;
    parts.leftArm.scale.set(sleeveWidth, 1, sleeveWidth);
    parts.rightArm.scale.set(sleeveWidth, 1, sleeveWidth);
    const garmentGeometry = this.garmentGeometries[appearance.outerwear];
    parts.leftSleeve.geometry = garmentGeometry.sleeve;
    parts.rightSleeve.geometry = garmentGeometry.sleeve;
    parts.leftHand.position.set(
      0,
      garmentGeometry.handOffset.y - 0.045,
      garmentGeometry.handOffset.z + 0.008,
    );
    parts.rightHand.position.copy(parts.leftHand.position);
    parts.leftLeg.position.x = 0.12 * body.hip;
    parts.rightLeg.position.x = -0.12 * body.hip;
    parts.leftTrouser.geometry =
      this.lowerBodyGeometries.legs[appearance.lowerGarment];
    parts.rightTrouser.geometry = parts.leftTrouser.geometry;
    parts.leftFootwear.geometry =
      this.lowerBodyGeometries.footwear[appearance.footwear];
    parts.rightFootwear.geometry = parts.leftFootwear.geometry;
    const legWidth = body.limb;
    parts.leftLeg.scale.set(legWidth, 1, legWidth);
    parts.rightLeg.scale.set(legWidth, 1, legWidth);
    parts.skirt.visible = appearance.lowerGarment === "skirt";
    parts.skirt.scale.set(body.hip, 1, body.hip);
    applyGarmentLayerSet(
      parts.garments,
      appearance.outerwear,
      body,
    );

    parts.head.scale.set(
      face.x * age.headScale,
      face.y * age.headScale,
      face.z * age.headScale,
    );
    parts.nose.scale.set(
      appearance.faceShape === "angular" ? 1.12 : 0.92,
      appearance.faceShape === "oval" ? 1.1 : 1,
      1,
    );
    const eyeSpacing = appearance.faceShape === "round" ? 0.073 : 0.067;
    parts.leftEye.position.x = eyeSpacing;
    parts.rightEye.position.x = -eyeSpacing;
    parts.leftEye.scale.setScalar(age.eyeScale);
    parts.rightEye.scale.setScalar(age.eyeScale);
    parts.leftLens.position.x = eyeSpacing;
    parts.rightLens.position.x = -eyeSpacing;
    parts.leftBrow.position.x = eyeSpacing;
    parts.rightBrow.position.x = -eyeSpacing;
    parts.mouth.scale.x = appearance.faceShape === "round" ? 0.9 : 1.05;

    const presentedHairStyle = resolvePresentedHairStyle(appearance);
    parts.hair.visible = presentedHairStyle !== "bald" ||
      appearance.facialHair !== "none";
    parts.hair.geometry = this.featureGeometries.hair[presentedHairStyle][
      appearance.facialHair
    ];
    parts.hair.position.set(0, 1.79, 0);
    parts.hair.scale.set(
      face.x * age.headScale,
      face.y * age.headScale,
      face.z * age.headScale,
    );

    const hasEyewear = appearance.eyewear !== "none";
    parts.leftLens.visible = hasEyewear;
    parts.rightLens.visible = hasEyewear;
    if (hasEyewear) {
      const lensGeometry = appearance.eyewear === "round_glasses"
        ? this.featureGeometries.eyewear.round
        : this.featureGeometries.eyewear.square;
      parts.leftLens.geometry = lensGeometry;
      parts.rightLens.geometry = lensGeometry;
    }
    parts.leftLens.scale.set(1, 1, 1);
    parts.rightLens.scale.set(1, 1, 1);
    parts.headwear.visible = appearance.headwear !== "none";
    if (appearance.headwear !== "none") {
      parts.headwear.geometry = this.featureGeometries.headwear[
        appearance.headwear
      ];
    }
    parts.headwear.position.set(0, 1.79, 0);
    parts.headwear.scale.set(
      face.x * age.headScale,
      face.y * age.headScale,
      face.z * age.headScale,
    );
    parts.neckwear.visible = appearance.neckwear !== "none";
    if (appearance.neckwear !== "none") {
      parts.neckwear.geometry = this.featureGeometries.neckwear[
        appearance.neckwear
      ];
    }
    parts.neckwear.scale.set(1, 1, 1);
    parts.carried.visible = appearance.carriedAccessory !== "none";
    if (appearance.carriedAccessory !== "none") {
      parts.carried.geometry = this.featureGeometries.carried[
        appearance.carriedAccessory
      ];
    }
    const carriedPlacement = APPEARANCE_PRESENTATION_CONFIG
      .carriedPlacementById[appearance.carriedAccessory];
    parts.carried.position.set(
      carriedPlacement.x,
      carriedPlacement.y,
      carriedPlacement.z,
    );
    parts.carried.scale.set(
      carriedPlacement.scaleX,
      carriedPlacement.scaleY,
      carriedPlacement.scaleZ,
    );
    this.materialSet.outerwear.color.setHex(appearance.colours.outerwear);
    setGarmentTrimColour(
      this.materialSet.trim,
      appearance.colours.outerwear,
    );
    this.materialSet.lower.color.setHex(appearance.colours.lower);
    this.materialSet.boot.color
      .setHex(appearance.colours.lower)
      .offsetHSL(0, -0.02, -0.11);
    this.materialSet.accent.color.setHex(appearance.colours.accent);
    this.materialSet.skin.color.setHex(
      APPEARANCE_PRESENTATION_CONFIG.skinToneById[appearance.skinTone],
    );
    this.materialSet.hair.color.setHex(
      APPEARANCE_PRESENTATION_CONFIG.hairColourById[appearance.hairColour],
    );
    this.materialSet.eye.color.setHex(
      APPEARANCE_PRESENTATION_CONFIG.eyeColourById[appearance.eyeColour],
    );
    this.object.userData.appearance = structuredClone(appearance);
  }

  setTransform(
    position: Readonly<RenderVector3>,
    yawRadians: number,
    motion: Readonly<MannequinMotionSample>,
    vehicleSeatRole: "driver" | "passenger" | null = null,
    vehicleSteeringRadians = 0,
  ): void {
    this.object.position.set(position.x, position.y, position.z);
    this.object.rotation.y = yawRadians;
    const pose = vehicleSeatRole === null
      ? this.motionConfig === undefined
      ? NEUTRAL_POSE
      : sampleMannequinPose(motion, this.motionConfig)
      : {
          state: vehicleSeatRole === "driver" ? "driving" as const : "seated" as const,
          leftArmX: vehicleSeatRole === "driver" ? -1.05 : -0.36,
          rightArmX: vehicleSeatRole === "driver" ? -1.05 : -0.36,
          leftArmZ: vehicleSeatRole === "driver"
            ? -0.22 + vehicleSteeringRadians * 0.42
            : 0.08,
          rightArmZ: vehicleSeatRole === "driver"
            ? 0.22 + vehicleSteeringRadians * 0.42
            : -0.08,
          leftLegX: -1.18,
          rightLegX: -1.18,
          torsoRoll: 0,
          torsoPitch: 0,
          weightShiftMeters: 0,
          rootBobMeters: 0,
          hipsYaw: 0,
        };
    this.currentMotionState = vehicleSeatRole !== null
      ? pose.state
      : this.motionConfig === undefined
      ? classifyMannequinMotion(motion, Number.EPSILON)
      : pose.state;
    this.presentationRoot.scale.set(
      1,
      this.statureScale * (
        vehicleSeatRole === null
          ? 1
          : CHARACTER_PRESENTATION_CONFIG.vehicleSeatedHeightScale
      ),
      1,
    );
    this.presentationRoot.position.y = vehicleSeatRole === null
      ? presentationGroundOffsetM(
        pose,
        this.appearance,
        this.appearanceParts.leftFootwear.geometry,
      ) + pose.rootBobMeters
      : CHARACTER_PRESENTATION_CONFIG.vehicleSeatedRootOffsetMeters;
    this.joints.leftArm.rotation.set(
      pose.leftArmX,
      0,
      pose.leftArmZ + CHARACTER_PRESENTATION_CONFIG.armRestSplayRadians,
    );
    this.joints.rightArm.rotation.set(
      pose.rightArmX,
      0,
      pose.rightArmZ - CHARACTER_PRESENTATION_CONFIG.armRestSplayRadians,
    );
    this.joints.leftLeg.rotation.set(pose.leftLegX, 0, 0);
    this.joints.rightLeg.rotation.set(pose.rightLegX, 0, 0);
    this.joints.torso.rotation.z = pose.torsoRoll;
    this.joints.torso.rotation.x = pose.torsoPitch;
    this.joints.torso.position.x = pose.weightShiftMeters;
    this.joints.hips.position.x = pose.weightShiftMeters * 0.45;
    this.joints.hips.rotation.y = pose.hipsYaw;
  }

  get motionState(): MannequinMotionState {
    return this.currentMotionState;
  }

  dispose(): void {
    const geometries = new Set<import("three").BufferGeometry>();
    this.object.traverse((child) => {
      if (child instanceof Mesh) {
        geometries.add(child.geometry);
      }
    });
    for (const style of Object.values(this.garmentGeometries)) {
      geometries.add(style.shell);
      geometries.add(style.trim);
      geometries.add(style.buttons);
      geometries.add(style.sleeve);
    }
    for (const geometry of [
      ...Object.values(this.featureGeometries.hair).flatMap((byFacialHair) =>
        Object.values(byFacialHair)
      ),
      ...Object.values(this.featureGeometries.headwear),
      ...Object.values(this.featureGeometries.neckwear),
      ...Object.values(this.featureGeometries.carried),
      ...Object.values(this.featureGeometries.eyewear),
      ...Object.values(this.lowerBodyGeometries.legs),
      ...Object.values(this.lowerBodyGeometries.footwear),
    ]) {
      geometries.add(geometry);
    }
    for (const geometry of geometries) {
      geometry.dispose();
    }
    for (const material of this.materials) {
      material.dispose();
    }
    this.object.removeFromParent();
  }
}
